import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../utils/api'

export interface HistoryItem {
  id: string
  prompt: string
  model_used: string
  media_type: string
  r2_object_key: string
  created_at: string
}

export function useHistoryData() {
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [mediaType, setMediaType] = useState<'image' | 'vision' | 'audio' | 'all'>('all')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [downloadingAll, setDownloadingAll] = useState(false)

  const itemsPerPage = 12

  // Read initial media type from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const mediaTypeParam = urlParams.get('media_type')
    if (mediaTypeParam && ['image', 'vision', 'audio', 'all'].includes(mediaTypeParam)) {
      setMediaType(mediaTypeParam as 'image' | 'vision' | 'audio' | 'all')
    }
  }, [])

  const loadHistory = useCallback(async (page: number = 1, type: 'image' | 'vision' | 'audio' | 'all' = 'all') => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.getUserGenerations(page, itemsPerPage, type)

      if (response.success && response.data) {
        setHistory(response.data.results || [])

        if (response.data.pagination) {
          setTotalItems(response.data.pagination.total || 0)
          setTotalPages(response.data.pagination.totalPages || 1)
          setCurrentPage(response.data.pagination.page || page)
        } else {
          setTotalItems(0)
          setTotalPages(1)
          setCurrentPage(page)
        }
      } else {
        throw new Error(response.error || 'Failed to load history')
      }
    } catch (err) {
      console.error('Failed to load history:', err)
      setError(err instanceof Error ? err.message : 'Failed to load history')
      setHistory([])
      setTotalItems(0)
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadHistory(1, mediaType)
  }, [loadHistory, mediaType])

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这条记录吗？此操作不可撤销。')) return

    try {
      const response = await apiClient.deleteGeneration(id)
      if (response.success) {
        loadHistory(currentPage)
      } else {
        throw new Error(response.error || 'Failed to delete')
      }
    } catch (err) {
      console.error('Failed to delete:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  const handleDownload = async (id: string) => {
    try {
      const imageUrl = apiClient.getImageUrl(id)
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `generated-image-${id}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to download image:', error)
      const imageUrl = apiClient.getImageUrl(id)
      window.open(imageUrl, '_blank')
    }
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      loadHistory(page)
    }
  }

  const handleCopyPrompt = async (id: string, prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy prompt:', err)
    }
  }

  const handleDownloadAll = async () => {
    if (!confirm(`确定要下载所有 ${totalItems} 个${mediaType === 'image' ? '图像' : mediaType === 'audio' ? '音频' : '文件'}吗？这可能需要一些时间。`)) {
      return
    }

    setDownloadingAll(true)
    let downloadedCount = 0
    let failedCount = 0

    try {
      const totalPagesToFetch = Math.ceil(totalItems / itemsPerPage)

      for (let page = 1; page <= totalPagesToFetch; page++) {
        try {
          const response = await apiClient.getUserGenerations(page, itemsPerPage, mediaType)

          if (response.success && response.data?.results) {
            const items = response.data.results

            for (let i = 0; i < items.length; i++) {
              const item = items[i]

              if (i > 0 || page > 1) {
                await new Promise(resolve => setTimeout(resolve, 300))
              }

              try {
                const imageUrl = apiClient.getImageUrl(item.id)
                const response = await fetch(imageUrl)
                const blob = await response.blob()
                const blobUrl = URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = blobUrl
                const extension = item.media_type === 'audio' ? 'wav' : 'png'
                link.download = `${item.media_type}-${item.id}.${extension}`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(blobUrl)

                downloadedCount++
              } catch (err) {
                console.error(`Failed to download item ${item.id}:`, err)
                failedCount++
              }
            }
          }
        } catch (err) {
          console.error(`Failed to fetch page ${page}:`, err)
        }
      }

      alert(`下载完成！\n成功: ${downloadedCount} 个\n失败: ${failedCount} 个`)
    } catch (err) {
      console.error('Batch download error:', err)
      alert('批量下载过程中出现错误，请重试')
    } finally {
      setDownloadingAll(false)
    }
  }

  return {
    history, loading, currentPage, totalPages, totalItems, error,
    mediaType, setMediaType, copiedId, viewMode, setViewMode, downloadingAll,
    loadHistory, handleDelete, handleDownload, handlePageChange,
    handleCopyPrompt, handleDownloadAll,
  }
}
